import React from 'react'
import { Badge, IconButton, Tooltip, useTheme } from '@material-ui/core'
import { Container, LeaseStatusBadge } from '../../components'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import DeleteForeverIcon from '@material-ui/icons/DeleteForever'
import {
    Damage,
    DamageStatus,
    DamageTransaction,
    getIssueCount,
} from '../../models'
import { currencyFormatter } from '../../helpers'
import { FileCopy, DateRange, Warning } from '@material-ui/icons'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    damageTransaction: DamageTransaction
    damage: Damage
    totalCost: number
    splitBy: number
    displayProblems: boolean
    removeDamageTransaction: () => void
    openMoveInModal: () => void
}

// Helper function for more readable date format
const formatDate = (date: Date): string => {
    const month = date.getMonth() + 1
    const day = date.getDate()
    const year = date.getFullYear()

    // Format: Jan 15, '23 (or current year format)
    const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ]
    const currentYear = new Date().getFullYear()
    const yearDisplay =
        currentYear === year ? '' : `, '${year.toString().slice(2)}`

    return `${monthNames[month - 1]} ${day}${yearDisplay}`
}

export const DamageTransactionRow = (props: Props) => {
    const {
        damageTransaction,
        totalCost,
        splitBy,
        damage,
        displayProblems,
    } = props

    const theme = useTheme()

    const isLeaseEntrata =
        Boolean(damageTransaction.lease.entrata_lease_id) ||
        damageTransaction.lease.parent_is_entrata === true

    const areaName = `${damageTransaction.lease.area.area_config_name} (${damageTransaction.lease.area.area_label})`

    const leaseStartDate = new Date(damageTransaction.lease.start_date)
    const leaseEndDate = new Date(damageTransaction.lease.end_date)

    const today = new Date()
    const endCutoff = new Date()

    let startDateStyle: React.CSSProperties = {}
    let endDateStyle: React.CSSProperties = {}

    const problemTextStyle: React.CSSProperties = {
        backgroundColor: 'red',
        color: 'white',
        borderRadius: 4,
        fontWeight: 'bold',
        padding: '0 2px',
    }

    if (displayProblems) {
        if (leaseStartDate > today) {
            startDateStyle = problemTextStyle
        }

        if (leaseEndDate < endCutoff) {
            endDateStyle = problemTextStyle
        }
    }

    const isTransactionSynced = damageTransaction.synced_id !== null
    let verifiedIcon: JSX.Element | null = null

    if (
        damage.status === DamageStatus.SYNC_FAILED ||
        damage.status === DamageStatus.ENTRATA_SYNCED ||
        damage.status === DamageStatus.SYNC_PENDING
    ) {
        verifiedIcon = isTransactionSynced ? (
            <Tooltip title={`Sync ID: ${damageTransaction.synced_id}`}>
                <CheckIcon fontSize="small" color="primary" />
            </Tooltip>
        ) : (
            <Tooltip title={'Sync Failed'}>
                <CloseIcon fontSize="small" color="secondary" />
            </Tooltip>
        )
    }

    const issueCount = getIssueCount(damageTransaction.lease)

    return (
        <Container
            style={{
                marginRight: theme.spacing(1),
                marginBottom: theme.spacing(1),
                backgroundColor: theme.palette.grey[50],
                border: `1px solid ${theme.palette.grey[400]}`,
                padding: theme.spacing(0.75),
                borderRadius: theme.shape.borderRadius,
                width: 234,
                flexDirection: 'column',
                height: 90,
                justifyContent: 'space-between',
            }}
        >
            {/* Top content section */}
            <div>
                {/* Tenant + Area */}
                <Container style={{ alignItems: 'center', marginBottom: 2 }}>
                    <Container style={{ alignItems: 'center', flex: 1 }}>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 120,
                            }}
                        >
                            {damageTransaction.lease.user.name}
                        </span>

                        {!isLeaseEntrata && (
                            <Tooltip title="Not Integrated Lease">
                                <Warning
                                    fontSize="small"
                                    style={{
                                        fontSize: 14,
                                        color: theme.palette.warning.main,
                                        marginLeft: 4,
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Container>

                    <span
                        style={{
                            ...typographyStyle(theme.typography.caption),
                            color: theme.palette.text.secondary,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {areaName}
                    </span>
                </Container>

                {/* Date Range */}
                <Container style={{ marginBottom: 2, alignItems: 'center' }}>
                    <Container
                        style={{
                            alignItems: 'center',
                            backgroundColor: theme.palette.grey[100],
                            padding: '0 4px',
                            borderRadius: 4,
                            height: 16,
                        }}
                    >
                        <DateRange
                            fontSize="small"
                            style={{
                                fontSize: 11,
                                color: theme.palette.text.secondary,
                                marginRight: 2,
                            }}
                        />
                        <span
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                ...startDateStyle,
                                fontSize: '0.7rem',
                            }}
                        >
                            {formatDate(leaseStartDate)}
                        </span>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                margin: '0 2px',
                                fontSize: '0.7rem',
                            }}
                        >
                            -
                        </span>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                ...endDateStyle,
                                fontSize: '0.7rem',
                            }}
                        >
                            {formatDate(leaseEndDate)}
                        </span>
                    </Container>
                </Container>
            </div>

            {/* Footer section: Status + Financial + Actions */}
            <Container
                style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Container style={{ alignItems: 'center' }}>
                    <div
                        style={{
                            transform: 'scale(0.85)',
                            transformOrigin: 'left center',
                            marginRight: 8,
                        }}
                    >
                        <LeaseStatusBadge
                            status={damageTransaction.lease.status}
                        />
                    </div>

                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle2),
                            fontWeight: 700,
                        }}
                    >
                        {currencyFormatter.format(totalCost / splitBy)}
                    </span>
                </Container>

                <Container style={{ alignItems: 'center' }}>
                    {verifiedIcon}

                    {issueCount > 0 && (
                        <Tooltip title="See Move In">
                            <IconButton
                                size="small"
                                onClick={props.openMoveInModal}
                                style={{ padding: 4 }}
                            >
                                <Badge
                                    badgeContent={issueCount}
                                    color="secondary"
                                    showZero={false}
                                >
                                    <FileCopy fontSize="small" />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip title="Cancel Transaction">
                        <IconButton
                            size="small"
                            onClick={() => {
                                if (
                                    confirm(
                                        'Would you like to remove this Transaction?',
                                    )
                                ) {
                                    props.removeDamageTransaction()
                                }
                            }}
                            disabled={
                                props.damage.status !== DamageStatus.PENDING &&
                                props.damage.status !==
                                    DamageStatus.PRE_APPROVED
                            }
                            style={{ padding: 4 }}
                        >
                            <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Container>
            </Container>
        </Container>
    )
}
